import React from 'react';

import { Box, Dialog, DialogClassKey, DialogProps, Slide, SlideProps, Typography, ClassNameMap } from '@mui/material';
import { withStyles } from 'tss-react/mui';

import { ModalTitle } from './modal-title.component';
import { ModalProvider } from './modal.context';

export type ModalProps = Omit<DialogProps, 'classes'> & {
  title?: string;
  subtitle?: React.ReactNode;
  classes?: Partial<ClassNameMap<DialogClassKey | 'disabled' | 'title' | 'subtitle'>>;
  disabled?: boolean;
  loading?: boolean;
  showCloseIcon?: boolean;
  fullScreen?: boolean;
  maxWidthPx?: string;
  disableBackdropClick?: boolean;
  onExited?: () => void;
};


export const FullScreenTransition: React.FC<SlideProps> = (props) => {
  return <Slide direction="up" {...props} />;
};


const ModalRaw: React.FC<ModalProps> = ({ title, subtitle, children, disabled, loading, onClose: _onClose, maxWidthPx, classes: _classes = {}, showCloseIcon = true, disableBackdropClick, ...props }) => {
  const onClose: DialogProps['onClose'] = _onClose ? (event, reason) => {
    if (disableBackdropClick && reason === 'backdropClick') {
      return;
    }
    _onClose(event, reason);
  } : _onClose;

  const handleClose = disabled || loading ? undefined : onClose;
  const { title: titleClassName, subtitle: subtitleClassName, ...classes } = _classes;

  const contextValue = React.useMemo(() => ({
    loading: loading,
    disabled: disabled,
  }), [ loading, disabled ]);

  const modalTitleOnClose = React.useMemo(
    () => showCloseIcon ? () => handleClose?.({}, 'backdropClick') : undefined,
    [ handleClose, showCloseIcon ]
  );

  const variantProps = React.useMemo(() => {
    if (props.fullScreen) {
      return {
        TransitionComponent: FullScreenTransition,
      };
    }

    return {};
  }, [ props.fullScreen ]);

  const modalTitle = React.useMemo(() => {
    if (title && subtitle) {
      return (
        <ModalTitle disabled={loading} className={titleClassName} onClose={modalTitleOnClose} data-testid="modal-title">
          {title}
          <Box mt={1}>
            <Typography variant="subtitle1" className={subtitleClassName} component="p" data-testid="modal-subtitle">
              {subtitle}
            </Typography>
          </Box>
        </ModalTitle>
      );
    }

    if (title) {
      return (
        <ModalTitle disabled={loading} className={titleClassName} onClose={modalTitleOnClose} data-testid="modal-title">
          {title}
        </ModalTitle>
      );
    }

    return null;
  }, [ loading, modalTitleOnClose, subtitle, subtitleClassName, title, titleClassName ]);

  return (
    <ModalProvider value={contextValue}>
      <Dialog
        {...props}
        classes={classes}
        onClose={handleClose}
        {...variantProps}
        PaperProps={{ ...props.PaperProps, style: { maxWidth: maxWidthPx, ...props.PaperProps?.style } }}
        TransitionProps={{
          ...props.TransitionProps,
          onExited: (...args) => {
            props.onExited?.();
            props.TransitionProps?.onExited?.(...args);
          }
        }}
      >
        {modalTitle}
        {children}
      </Dialog>
    </ModalProvider>
  );
};

export const Modal = withStyles(ModalRaw, theme => ({
  root: {},
  title: {},
  subtitle: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  paper: {
    margin: theme.spacing(1),
  },
  paperFullWidth: {
    width: '100%',
    maxWidth: 'calc(100% - 16px)',
  },
  paperWidthSm: {
    maxWidth: '400px',
  },
  paperWidthMd: {
    maxWidth: '680px',
  },
  paperWidthLg: {
    maxWidth: '912px',
  },
  paperWidthXl: {
    maxWidth: '1245px',
  },
  paperFullScreen: {
    maxWidth: '100%',
    margin: 'initial',
  },
}));
